import {type ReactNode, useMemo} from 'react'
import {useFormLayout} from '@/hooks/useFormLayout'
import type {EntityLayoutField, LayoutSection} from '@/types/form-layout'
import {COL_SPAN_CLASS} from '@/types/form-layout'
import {LayoutActiveIndicator} from './LayoutActiveIndicator'

/**
 * Extracted field overrides from the layout for a given entity field.
 */
export interface EntityFieldOverrides {
    label?: string
    placeholder?: string
    defaultValue?: string
    disabled?: boolean
    hidden?: boolean
}

/**
 * Hook that returns layout-driven overrides for entity fields.
 * Use this in wizard steps to apply label/placeholder/defaultValue from the layout designer.
 */
export function useEntityFieldOverrides(useCaseKey: string): Record<string, EntityFieldOverrides> {
    const {data: layout} = useFormLayout(useCaseKey)

    return useMemo(() => {
        if (!layout) return {}
        const overrides: Record<string, EntityFieldOverrides> = {}
        for (const section of layout.layoutSchema.sections) {
            for (const field of section.fields) {
                if (field.source === 'entity') {
                    const ef = field as EntityLayoutField
                    overrides[ef.fieldKey] = {
                        label: ef.label ?? undefined,
                        placeholder: ef.placeholder ?? undefined,
                        defaultValue: ef.defaultValue ?? undefined,
                        disabled: ef.disabled ?? undefined,
                        hidden: ef.hidden ?? undefined,
                    }
                }
            }
        }
        return overrides
    }, [layout])
}

/**
 * Renders entity fields from ReactNode slots, arranged according to a saved form layout.
 *
 * SECURITY: This component MUST NOT use dangerouslySetInnerHTML for any value sourced from the
 * layout schema.
 *
 * FALLBACK: If no layout exists for useCaseKey (404), renders all entity fields in the order
 * they appear in entityFields (insertion order). This is the zero-downtime fallback.
 */
interface EntityLayoutRendererProps {
    useCaseKey: string
    entityFields: Record<string, ReactNode>
    templateContext?: Record<string, string>
}

function evaluateVisibility(field: EntityLayoutField): boolean {
    if (!field.visibilityCondition) return true
    // Visibility of entity fields is controlled by the parent (who decides which ReactNodes to include).
    // The renderer always shows them; the parent omits fields from entityFields when conditions aren't met.
    return true
}

interface SectionGridProps {
    section: LayoutSection
    entityFields: Record<string, ReactNode>
}

function SectionGrid({section, entityFields}: SectionGridProps) {
    const entityFieldsInSection = section.fields.filter(
        (f): f is EntityLayoutField => f.source === 'entity'
    )

    return (
        <div className="space-y-2">
            {section.label && (
                <h3 className="text-sm font-semibold text-foreground" data-testid={`entity-section-title-${section.key}`}>{section.label}</h3>
            )}
            <div className="grid grid-cols-12 gap-3">
                {entityFieldsInSection.map((field, idx) => {
                    if (field.hidden) return null
                    if (!evaluateVisibility(field)) return null

                    const node = entityFields[field.fieldKey]
                    if (node === undefined) return null

                    const defaultSpan = Math.floor(12 / (section.columns ?? 1))
                    const colSpan = Math.min(Math.max(field.colSpan ?? defaultSpan, 1), 12)

                    return (
                        <div
                            key={idx}
                            className={COL_SPAN_CLASS[colSpan]}
                        >
                            {node}
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

/**
 * EntityLayoutRenderer — renders entity fields from ReactNode slots according to a saved layout.
 */
export function EntityLayoutRenderer({useCaseKey, entityFields}: EntityLayoutRendererProps) {
    const {data: layout, isLoading, isError} = useFormLayout(useCaseKey)

    // While loading: show fields in default order (no flash of blank)
    if (isLoading) {
        return (
            <div className="space-y-4">
                {Object.entries(entityFields).map(([key, node]) => (
                    <div key={key}>{node}</div>
                ))}
            </div>
        )
    }

    // Error (non-404) or no layout: fall through to default rendering
    if (isError || !layout) {
        return (
            <div className="space-y-4">
                {Object.entries(entityFields).map(([key, node]) => (
                    <div key={key}>{node}</div>
                ))}
            </div>
        )
    }

    // Layout-driven rendering
    return (
        <div className="space-y-4">
            <LayoutActiveIndicator useCaseKey={useCaseKey} />
            {layout.layoutSchema.sections.map((section) => (
                <SectionGrid
                    key={section.key}
                    section={section}
                    entityFields={entityFields}
                />
            ))}
        </div>
    )
}
